import { repeat } from "@@/utilities/repeat";

import { now } from "@@/shared/date-time/date-time";
import { type Discount } from "@@/shared/discounts/discount";
import { metaContentFactory } from "@@/shared/meta-content/meta-content";
import { type PickedOptional } from "@@/shared/optionals/picked-optional";
import { orderItemsFromSku_V2 } from "@@/shared/orders/order-items/order-items-from-sku-v2";
import { type Price } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { GiftCard_Product } from "@@/shared/products/product";
import { type Quantity } from "@@/shared/units-and-quantities/quantity";
import type { Sku } from "../../products/sku";
import { orderItemIdFactory_V2, type OrderItemId } from "./order-item-id";
import type {
    OrderItemAccommodation_V2,
    OrderItemAccommodationRateItem_V2,
    OrderItemBookableQuantity_V2,
    OrderItemOptional_V2,
} from "./order-item-v2";
import {
    orderItemBookableFactory_V2,
    orderItemBookableZodSchema_V2,
    orderItemBuyableFactory_V2,
    orderItemBuyableZodSchema_V2,
    orderItemGiftCardFactory_V2,
    orderItemGiftCardZodSchema_V2,
    orderItemVoucherFactory_V2,
    orderItemVoucherZodSchema_V2,
    orderItemZodSchema_V2,
    type OrderItem_V2,
    type OrderItemBookable_V2,
    type OrderItemBuyable_V2,
    type OrderItemGiftCard_V2,
    type OrderItemVoucher_V2,
} from "./order-item-v2";

import { zeroPrice } from "@@/shared/prices/price";

type OrderItem = OrderItem_V2;
const orderItemZodSchema = orderItemZodSchema_V2;

/**
 * Sum all prices available in cart item to get the
 * full and complete price of the cart item
 * @param {{
 *     sku: Sku,
 *     pickedOptions: PickedOptional[],
 * }} params
 * @returns {Price}
 */
const calculateOrderItemPrice = (params: {
    sku: Sku;
    pickedOptions: PickedOptional[];
}): Price => {
    const pricesToSum = [
        params.sku.acquire.price,
        ...(params.pickedOptions?.flatMap(pickedOption =>
            repeat(pickedOption, pickedOption.quantity.value).map(_ =>
                pickedOption.optional._type === "OPTIONAL"
                    ? pickedOption.optional.price
                    : zeroPrice,
            ),
        ) || []),
    ];
    const itemPrice = sumPrices(pricesToSum);

    return itemPrice;
};

const orderItemFactory = {
    giftCard: (params: {
        giftCard: GiftCard_Product;
        selectedPrice: Price;
        quantity: Quantity;
        discounts: Discount[];
        orderItemId?: OrderItemId;
        messageToProvider?: string;
    }) => {
        const giftCard = params.giftCard;
        const orderItemId = params.orderItemId ?? orderItemIdFactory_V2();
        const sku = giftCard.skus[0];
        if (!sku) {
            throw new Error("No sku found for gift card");
        }

        const pricePerItem = params.selectedPrice;
        const price = sumPrices(repeat(pricePerItem, params.quantity.value));

        const orderItem: OrderItemGiftCard_V2 = {
            _id: orderItemId,
            _type: "ORDER_ITEM_V2",
            _createdAt: now(),
            acquireType: "GIFTCARD",
            skuId: sku._id,
            productId: giftCard._id,
            discounts: params.discounts,
            providerId: giftCard.providerId,
            articleNumber: sku.articleNumber,
            messageToProvider: params.messageToProvider || null,
            meta: metaContentFactory({
                ...sku.meta,
                title: giftCard.meta.title,
                kicker: giftCard.meta.kicker,
                subtitle: sku.meta.subtitle || giftCard.meta.subtitle,
                description: giftCard.meta.description,
                images: giftCard.meta.images, //maybe sku but we don't have it.
            }),
            amount: sku.acquire.amount,
            pricePerItem: params.selectedPrice,
            price: price,
            originalPricePerItem: pricePerItem,
            originalPrice: price,
            quantity: params.quantity,
        };
        return orderItem;
    },
};

type OrderItemBookable = OrderItemBookable_V2;
type OrderItemBuyable = OrderItemBuyable_V2;
type OrderItemGiftCard = OrderItemGiftCard_V2;
type OrderItemVoucher = OrderItemVoucher_V2;
type OrderItemBookableQuantity = OrderItemBookableQuantity_V2;
type OrderItemOptional = OrderItemOptional_V2;

type OrderItemAccommodation = OrderItemAccommodation_V2;
type OrderItemAccommodationRateItem = OrderItemAccommodationRateItem_V2;

const orderItemBookableZodSchema = orderItemBookableZodSchema_V2;
const orderItemBuyableZodSchema = orderItemBuyableZodSchema_V2;
const orderItemVoucherZodSchema = orderItemVoucherZodSchema_V2;
const orderItemGiftCardZodSchema = orderItemGiftCardZodSchema_V2;

const orderItemBookableFactory = orderItemBookableFactory_V2;
const orderItemBuyableFactory = orderItemBuyableFactory_V2;
const orderItemGiftCardFactory = orderItemGiftCardFactory_V2;
const orderItemVoucherFactory = orderItemVoucherFactory_V2;
const orderItemsFromSku = orderItemsFromSku_V2;

export {
    calculateOrderItemPrice,
    orderItemBookableFactory,
    orderItemBookableZodSchema,
    orderItemBuyableFactory,
    orderItemBuyableZodSchema,
    orderItemFactory,
    orderItemGiftCardFactory,
    orderItemGiftCardZodSchema,
    orderItemsFromSku,
    orderItemVoucherFactory,
    orderItemVoucherZodSchema,
    orderItemZodSchema,
};
export type {
    OrderItem,
    OrderItemAccommodation,
    OrderItemAccommodationRateItem,
    OrderItemBookable,
    OrderItemBookableQuantity,
    OrderItemBuyable,
    OrderItemGiftCard,
    OrderItemOptional,
    OrderItemVoucher,
};
